import crypto from "node:crypto";

import mongoose from "mongoose";

import { OTP } from "../models";
import { OTP_TYPES, type OtpType } from "../models/constants/model-constants";
import { logger } from "../utils/logger";
import { emailService } from "./email-service";

// OTP purposes (mapped to model OtpType)
export enum OTPPurpose {
  EMAIL_VERIFICATION = "email_verification",
  PASSWORD_RESET = "password_reset",
}

// Map OTPPurpose to model OtpType
const purposeToOtpType: Record<OTPPurpose, OtpType> = {
  [OTPPurpose.EMAIL_VERIFICATION]: OTP_TYPES.EMAIL_VERIFICATION,
  [OTPPurpose.PASSWORD_RESET]: OTP_TYPES.PASSWORD_RESET,
};

// OTP configuration
const OTP_EXPIRY_MINUTES = {
  [OTPPurpose.EMAIL_VERIFICATION]: 10,
  [OTPPurpose.PASSWORD_RESET]: 5,
};

// Maximum OTP attempts
const MAX_OTP_ATTEMPTS = 3;
const OTP_COOLDOWN_MINUTES = 2;

class OTPService {
  /**
   * Generate a random OTP
   */
  private generateOTP(): string {
    return crypto.randomInt(100_000, 999_999).toString();
  }

  /**
   * Calculate OTP expiry time
   */
  private getExpiryTime(purpose: OTPPurpose): Date {
    const expiryMinutes = OTP_EXPIRY_MINUTES[purpose];
    return new Date(Date.now() + expiryMinutes * 60 * 1000);
  }

  /**
   * Check if user can receive a new OTP (cooldown check)
   */
  private async canSendOTP(
    email: string,
    purpose: OTPPurpose,
  ): Promise<{ canSend: boolean; reason?: string }> {
    const cooldownTime = new Date(
      Date.now() - OTP_COOLDOWN_MINUTES * 60 * 1000,
    );

    const recentOTP = await OTP.findOne({
      email: email.toLowerCase(),
      purpose,
      createdAt: { $gte: cooldownTime },
    }).sort({ createdAt: -1 });

    if (recentOTP) {
      const remainingTime = Math.ceil(
        (recentOTP.createdAt.getTime() +
          OTP_COOLDOWN_MINUTES * 60 * 1000 -
          Date.now()) /
          1000 /
          60,
      );
      return {
        canSend: false,
        reason: `Please wait ${remainingTime} minute(s) before requesting a new OTP`,
      };
    }

    return { canSend: true };
  }

  /**
   * Generate and send OTP for email verification
   */
  async sendEmailVerificationOTP(
    email: string,
    userId: mongoose.Types.ObjectId,
    name?: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Validate required parameters
      if (!email || !userId) {
        logger.error("Email verification OTP: email and userId are required", {
          email,
          userId,
        });
        return {
          success: false,
          message: "Invalid parameters for OTP generation",
        };
      }

      const normalizedEmail = email.toLowerCase();
      const otpType = purposeToOtpType[OTPPurpose.EMAIL_VERIFICATION];

      // Use model's static method to generate OTP
      const otpRecord = await OTP.generateForUser(
        userId,
        otpType,
        normalizedEmail,
        undefined, // phone
        OTP_EXPIRY_MINUTES[OTPPurpose.EMAIL_VERIFICATION],
        MAX_OTP_ATTEMPTS,
      );

      // Send email
      const emailSent = await emailService.sendRegistrationOTP({
        email: normalizedEmail,
        name,
        otp: otpRecord.otp,
        purpose: "registration",
        expiryMinutes: OTP_EXPIRY_MINUTES[OTPPurpose.EMAIL_VERIFICATION],
      });

      if (!emailSent) {
        // Mark OTP as used if email failed
        otpRecord.isUsed = true;
        await otpRecord.save();

        return {
          success: false,
          message: "Failed to send verification email. Please try again.",
        };
      }

      logger.info(`Email verification OTP sent to ${normalizedEmail}`);

      return {
        success: true,
        message: "Verification code sent to your email",
      };
    } catch (error) {
      logger.error("Failed to send email verification OTP:", error);
      return {
        success: false,
        message: "Failed to send verification code. Please try again.",
      };
    }
  }

  /**
   * Generate and send OTP for password reset
   */
  async sendPasswordResetOTP(
    email: string,
    userId: mongoose.Types.ObjectId,
    name?: string,
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Validate required parameters
      if (!email || !userId) {
        logger.error("Password reset OTP: email and userId are required", {
          email,
          userId,
        });
        return {
          success: false,
          message: "Invalid parameters for OTP generation",
        };
      }

      const normalizedEmail = email.toLowerCase();
      const otpType = purposeToOtpType[OTPPurpose.PASSWORD_RESET];

      // Use model's static method to generate OTP
      const otpRecord = await OTP.generateForUser(
        userId,
        otpType,
        normalizedEmail,
        undefined, // phone
        OTP_EXPIRY_MINUTES[OTPPurpose.PASSWORD_RESET],
        MAX_OTP_ATTEMPTS,
      );

      // Send email
      const emailSent = await emailService.sendPasswordResetOTP({
        email: normalizedEmail,
        name,
        otp: otpRecord.otp,
        purpose: "password_reset",
        expiryMinutes: OTP_EXPIRY_MINUTES[OTPPurpose.PASSWORD_RESET],
      });

      if (!emailSent) {
        // Mark OTP as used if email failed
        otpRecord.isUsed = true;
        await otpRecord.save();

        return {
          success: false,
          message: "Failed to send password reset email. Please try again.",
        };
      }

      logger.info(`Password reset OTP sent to ${normalizedEmail}`);

      return {
        success: true,
        message: "Password reset code sent to your email",
      };
    } catch (error) {
      logger.error("Failed to send password reset OTP:", error);
      return {
        success: false,
        message: "Failed to send password reset code. Please try again.",
      };
    }
  }

  /**
   * Verify OTP
   */
  async verifyOTP(
    email: string,
    otp: string,
    purpose: OTPPurpose,
    userId: mongoose.Types.ObjectId,
  ): Promise<{
    success: boolean;
    message: string;
    attemptsLeft?: number;
  }> {
    try {
      const otpType = purposeToOtpType[purpose];

      // Use model's static method to verify OTP
      const result = await OTP.verifyOTP(userId, otpType, otp, {
        ipAddress: undefined,
        userAgent: undefined,
        purpose: purpose,
      });

      if (result.success) {
        logger.info(
          `OTP verified successfully for user ${userId.toString()}, purpose: ${purpose}`,
        );
      }

      return {
        success: result.success,
        message: result.message,
        attemptsLeft: result.message.includes("attempts remaining")
          ? Number.parseInt(
              /(\d+) attempts remaining/.exec(result.message)?.[1] || "0",
            )
          : undefined,
      };
    } catch (error) {
      logger.error("Failed to verify OTP:", error);
      return {
        success: false,
        message: "Failed to verify code. Please try again.",
      };
    }
  }

  /**
   * Clean up expired OTPs (should be run periodically)
   */
  async cleanupExpiredOTPs(): Promise<void> {
    try {
      const result = await OTP.deleteMany({
        $or: [
          { expiresAt: { $lt: new Date() } },
          {
            isUsed: true,
            createdAt: { $lt: new Date(Date.now() - 24 * 60 * 60 * 1000) }, // 24 hours old
          },
        ],
      });

      logger.info(`Cleaned up ${result.deletedCount} expired OTP records`);
    } catch (error) {
      logger.error("Failed to cleanup expired OTPs:", error);
    }
  }

  /**
   * Get OTP statistics for monitoring
   */
  async getOTPStats(email?: string): Promise<any> {
    try {
      const matchStage = email ? { email: email.toLowerCase() } : {};

      const stats = await OTP.aggregate([
        { $match: matchStage },
        {
          $group: {
            _id: null,
            total: { $sum: 1 },
            used: { $sum: { $cond: ["$isUsed", 1, 0] } },
            expired: {
              $sum: {
                $cond: [{ $lt: ["$expiresAt", new Date()] }, 1, 0],
              },
            },
            byPurpose: {
              $push: {
                purpose: "$purpose",
                isUsed: "$isUsed",
                isExpired: { $lt: ["$expiresAt", new Date()] },
              },
            },
          },
        },
      ]);

      return stats[0] || { total: 0, used: 0, expired: 0, byPurpose: [] };
    } catch (error) {
      logger.error("Failed to get OTP stats:", error);
      return { total: 0, used: 0, expired: 0, byPurpose: [] };
    }
  }
}

// Export singleton instance
export const otpService = new OTPService();
